import { Component, OnInit, OnDestroy, ViewChild, TemplateRef, AfterViewInit, Inject, Injector } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';

import { BehaviorSubject, Subscription, filter } from 'rxjs';

import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';

import { DistributionService } from '@Common/TargetingShared/services/distribution.service';
import { TableColumnModel } from '@Common/Forms/models/table-column.model';
import { TableColumnInputEnum } from '@Common/Forms/enums/table-column-input-type.enum';
import { emailSeparatorsRegex, validateEmails } from '@Common/Misc/helpers/formatting/email.helper';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { TargetingPreferencesService } from '@Common/TargetingShared/services/targeting-preferences.service';
import { DistributionQuotaInfo } from '@Common/Charts/models/distribution.quota.info';
import { ProgressChartModel } from '@Common/Charts/models/progressChart/progress-chart.model';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';

import { MediaSubscriptionService } from '@Monitor/Core/services/media-subscription.service';

import { TargetingSearchesService } from '../../../services/targeting-searches.service';
import { RecipientInformationModel } from '../../../models/recipient-information.model';
import { TargetingListService } from '../../../services/targeting-list.service';
import { RecipientType } from '../../../enums/recipient-type.enum';
import { DistributionSourceType } from '../../../enums/distribution-source-type.enum';

import { RecipientEditorService, RecipientRow } from './recipient-editor.service';
import { RecommendedRecipientPanelComponent } from './RecommendedRecipient/recommended-recipient-panel.component';


@Component({
    selector: 'agpr-distribution-recipient',
    templateUrl: './recipient-editor.component.html',
    styleUrls: ['./recipient-editor.component.scss'],
    animations: [
        trigger('slide', [
            transition(':enter', [
                style({ height: 0, opacity: 0, overflow: 'hidden' }),
                animate('100ms', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('100ms', style({ height: 0, opacity: 0 }))
            ]),
        ])
    ],
    standalone: false
})
export class RecipientEditorComponent implements OnInit, OnDestroy, AfterViewInit {
    hideSlideOverlay: boolean = false;

    subscriptions: Subscription[] = [];
    public recipientInformation: RecipientInformationModel;

    public recipientRows: RecipientRow[] = [];

    distributionQuotaInfo: DistributionQuotaInfo;
    quotaNoticeChart: ProgressChartModel;
    remainingUsagePercentage: number;
    remainingUsageCount: number;
    collapsed: boolean = true;
    recipientsAvailable: string = '';

    hasFilters: boolean = false;

    sourceType: DistributionSourceType = DistributionSourceType.lists;

    quickFilterPlaceholder: string = '';

    emailValidationFunction: Function = validateEmails;
    public splitOnPattern: RegExp = emailSeparatorsRegex;

    noContentMessage: string;
    viewIconLabel: string = 'Discover.recipientEditor.labels.viewList';
    userTimeZone: string;
    dateFormat: string;

    nameColumn: TableColumnModel = {
        type: TableColumnInputEnum.checkbox,
        heading: 'Discover.recipientEditor.name',
        field: 'selected',
        labelField: 'name',
        sortable: true,
        sortType: 'text',
        width: 255,
        tooltipField: 'createdUser',
    };

    countColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        heading: 'Discover.recipientEditor.numberOfContacts',
        field: 'count',
        sortable: false,
    };

    columnDefs: TableColumnModel[] = [];

    selectedTabIndex = DistributionSourceType.lists;

    expandedStates = {};

    openAiPermission: boolean = false;

    distributionId: number;

    @ViewChild('countFetchingCell') public countCellTemplate: TemplateRef<any>;

    readonly buttonTypes = ButtonTypesEnum;
    readonly sourceTypes = DistributionSourceType;
    readonly recipientTypes = RecipientType;
    readonly messageSeverityEnum = messageSeverityEnum;

    readonly recipientIcon = 'assets/images/recipient_ai_icon.png';

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public coreDialogService: CoreDialogService,
        public distributionService: DistributionService,
        public localizationService: LocalizationService,
        public targetingListService: TargetingListService,
        public targetingSearchesService: TargetingSearchesService,
        public recipientEditorService: RecipientEditorService,
        public targetingPreferencesService: TargetingPreferencesService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public currentUserStateService: CurrentUserStateService,
        public injector: Injector,
    ) {
        // Force reload because the stored availableDistribution could be stale
        // for example if the user sent a distrib in another tab or a refund occured
        // as a result of a failed distrib
        this.targetingPreferencesService.reloadPreferences();

        this.expandedStates[RecipientType.list] = true;
        this.expandedStates[RecipientType.search] = true;
        this.expandedStates[RecipientType.mailingList] = true;
        this.expandedStates[RecipientType.email] = true;
        this.expandedStates[RecipientType.portfolio] = true;
        this.expandedStates[RecipientType.individual] = true;

        this.noContentMessage = this.localizationService.transform('Discover.recipientEditor.noContentMessage.list');

        this.subscriptions.push(
            this.recipientEditorService.getRecipientRows().subscribe(x => {
                this.recipientRows = x;
            }),
            this.recipientEditorService.getFilters().subscribe(filters => {
                this.hasFilters = filters && !!filters.find(filterItem => filterItem.chips.length > 0);
            }),
            this.recipientEditorService.getCurrentRecipientInformation().pipe(filter(x => !!x)).subscribe(recipientInfo => {
                this.recipientInformation = recipientInfo;
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.userTimeZone = prefs.timeZone;
                this.dateFormat = prefs.getDateFormatString(true, true, true);
            }),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                }
            }),
            this.distributionService.getFocusedResource().pipe(filter(x => !!x)).subscribe(distribution => this.distributionId = distribution?.id),
        );
    }

    ngOnInit() {
        this.quickFilterPlaceholder = this.localizationService.transform('Discover.recipientEditor.sourceTypeFilter.placeholder.lists');
    }

    ngAfterViewInit() {
        requestAnimationFrame(() => {
            this.countColumn.customTemplate = this.countCellTemplate;
            this.columnDefs = [
                this.nameColumn,
                this.countColumn
            ];
            this.tabChange(DistributionSourceType.lists);
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    updateRecipients() {
        this.recipientEditorService.updateRecipients();
    }

    selectionChanged(item: RecipientRow) {
        if (this.sourceType === DistributionSourceType.searches && item.count === null) {
            this.fetchSearchCount(item);
        } else {
            if (item.selected) {
                this.recipientEditorService.addRecipient(this.sourceType, item);
                this.expandedStates[this.sourceType] = true;
            } else {
                this.recipientEditorService.removeRecipientByCheckbox(this.sourceType, item);
            }
        }
    }

    fetchSearchCount(row: RecipientRow) {
        row.countLoading = true;

        this.targetingSearchesService.getSearchCount(row.id, null).subscribe(count => {
            row.count = count;
            row.countLoading = false;
            if (row.selected) {
                this.recipientEditorService.addRecipient(this.sourceType, row);
                this.expandedStates[this.sourceType] = true;
            }
        });
    }

    onDistributionSourceTypeChanged(value: DistributionSourceType) {
        this.sourceType = value;
        switch (this.sourceType) {
            case DistributionSourceType.lists:
                this.noContentMessage = this.localizationService.transform('Discover.recipientEditor.noContentMessage.list');
                this.quickFilterPlaceholder = this.localizationService.transform('Discover.recipientEditor.sourceTypeFilter.placeholder.lists');
                this.viewIconLabel = 'Discover.recipientEditor.labels.viewList';
                break;
            case DistributionSourceType.searches:
                this.noContentMessage = this.localizationService.transform('Discover.recipientEditor.noContentMessage.search');
                this.quickFilterPlaceholder = this.localizationService.transform('Discover.recipientEditor.sourceTypeFilter.placeholder.searches');
                this.viewIconLabel = 'Discover.recipientEditor.labels.viewSearch';
                break;
            case DistributionSourceType.openAI:
                this.noContentMessage = 'no content message';
                this.quickFilterPlaceholder = 'quickFilterPlaceholder';
                this.viewIconLabel = 'viewIconLabel';
                break;
            // currently not being used, but might add it down the road
            // case DistributionSourceType.portfolios:
            //     // we can assume the portfolios exist
            //     this.viewIconLabel = 'Discover.recipientEditor.labels.viewList';
            //     break;
            // case DistributionSourceType.mailingLists:
            //     this.noContentMessage = this.localizationService.transform('Discover.recipientEditor.noContentMessage.mailingList');
            //     this.quickFilterPlaceholder = this.localizationService.transform('Discover.recipientEditor.sourceTypeFilter.placeholder.mailingLists');
            //     break;
        }
        this.recipientEditorService.changeSourceType(this.sourceType);
    }

    createNewRecipientGroup(sourceType: DistributionSourceType) {
        switch (sourceType) {
            case DistributionSourceType.lists:
                window.open(this.appBaseHref + 'Discover/targetinglists', '_blank');
                break;
            case DistributionSourceType.searches:
                window.open(this.appBaseHref + 'Discover/targetingsearches/search', '_blank');
                break;
        }
    }

    // currently not being used, but might add it down the road
    // openManageMailingLists() {
    //     const overlayContainer = document.getElementById('main-content');
    //     const animationTrigger = new BehaviorSubject(
    //         new FullProfileAnimationModel(
    //             FullProfileDialogAnimationsEnum.slideInOutAnimation,
    //             OverlayAnimationStateEnum.open
    //         )
    //     );
    //     const io = {
    //         inputs: {
    //             animationTrigger,
    //             overlayContainer
    //         }
    //     };
    //     this.coreDialogService.open(ManageMailingListsDialogComponent, io);
    // }

    view(row: RecipientRow) {
        if (this.sourceType === DistributionSourceType.lists) {
            window.open(this.appBaseHref + `Discover/targetinglists/list/${row?.id}/results`, '_blank');
        } else if (this.sourceType === DistributionSourceType.searches) {
            window.open(this.appBaseHref + `Discover/targetingsearches/search/${row?.id}/results`, '_blank');
        }
    }

    /**
     * Handles changes to selected search type tab
     */
    public tabChange(tabNumber) {
        this.selectedTabIndex = tabNumber;
        this.onDistributionSourceTypeChanged(this.selectedTabIndex);
    }

    findContacts() {
        this.tabChange(DistributionSourceType.openAI);

        const overlayContainer = document.getElementById('main-content');

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const dialogIO = {
            inputs: {
                animationTrigger,
                overlayContainer,
                distributionId: this.distributionId,
            }
        };
        this.coreDialogService.open(RecommendedRecipientPanelComponent, dialogIO, this.injector);
    }
}
